import * as fabric from 'fabric';
import { RecordingEvent, HistoryState } from '../../types/recording';
import { Tool } from '../../types';
import { createFabricObject } from './canvasUtils';
import { ProgressiveStrokeProcessor } from './ProgressiveStrokeProcessor';

export class ActionProcessor {
  private canvas: fabric.Canvas;
  private strokeMap: Map<string, fabric.Object>;
  private progressiveStrokeProcessor: ProgressiveStrokeProcessor;
  private strokePoints: Map<string, { x: number; y: number }[]> = new Map();
  private undoStack: HistoryState[];
  private redoStack: HistoryState[];
  private maxHistorySize: number = 50;
  // Store stroke metadata for recreation after reset
  private strokeMetadata: Map<string, { tool: string; color: string; width: number; startTime: number }> = new Map();
  // Performance metrics
  private eventsProcessedCount: number = 0;
  private lastMetricsReset: number = Date.now();
  
  constructor(canvas: fabric.Canvas) {
    this.canvas = canvas;
    this.strokeMap = new Map();
    this.progressiveStrokeProcessor = new ProgressiveStrokeProcessor(canvas, this.strokeMap);
    this.undoStack = [];
    this.redoStack = [];
  }
  
  // Process multiple events in a batch for better performance
  processBatch(events: RecordingEvent[]): void {
    // Disable rendering during batch processing
    this.canvas.renderOnAddRemove = false;
    
    for (const event of events) {
      this.processEventInternal(event);
      this.eventsProcessedCount++;
    }
    
    // Re-enable rendering and render once
    this.canvas.renderOnAddRemove = true;
    this.canvas.requestRenderAll();
  }
  
  // Process a recording event
  processEvent(event: RecordingEvent): void {
    this.processEventInternal(event);
    this.eventsProcessedCount++;
    this.canvas.requestRenderAll();
  }
  
  private processEventInternal(event: RecordingEvent): void {
    // Clear redo stack on new action
    if (event.type !== 'undo' && event.type !== 'redo') {
      this.redoStack = [];
    }
    
    switch (event.type) {
      case 'stroke_start':
        this.handleStrokeStart(event);
        break;
        
      case 'stroke_add':
        this.handleStrokeAdd(event);
        break;
        
      case 'stroke_end':
        this.handleStrokeEnd(event);
        break;
        
      case 'add_stroke':
        this.handleAddStroke(event);
        break;
        
      case 'stroke_delete':
        this.handleStrokeDelete(event);
        break;
        
      case 'stroke_transform':
        this.handleStrokeTransform(event);
        break;
        
      case 'clear':
        this.handleClear();
        break;
        
      case 'undo':
        this.handleUndo();
        break;
        
      case 'redo':
        this.handleRedo();
        break;
        
      case 'tool_change':
      case 'color_change':
      case 'width_change':
        // These don't affect canvas directly
        break;
        
      case 'question_change':
        this.handleQuestionChange(event);
        break;
        
      case 'shape_preview':
        this.handleShapePreview(event);
        break;
        
      case 'laser_start':
        this.handleLaserStart(event);
        break;
        
      case 'laser_move':
        this.handleLaserMove(event);
        break;
        
      case 'laser_end':
        this.handleLaserEnd(event);
        break;
    }
  }
  
  // Add stroke to canvas
  private handleAddStroke(event: RecordingEvent): void {
    if (event.type !== 'add_stroke') return;
    
    const stroke = event.data.stroke;
    
    // Skip if we already have this stroke from progressive events
    if (this.strokeMap.has(stroke.id)) {
      return;
    }
    
    // Skip eraser strokes
    if (stroke.tool === 'eraser') {
      return;
    }
    
    // Check if we already have this stroke from progressive events
    if (!this.progressiveStrokeProcessor.hasStroke(stroke.id)) {
      // Only set up if we don't have progressive data
      // This means it's an old recording or a shape stroke
      
      this.progressiveStrokeProcessor.setStrokePoints(
        stroke.id,
        stroke.points,
        event.timestamp
      );
      
      // Set stroke properties
      this.progressiveStrokeProcessor.setStrokeProperties(
        stroke.id,
        stroke.color,
        stroke.width,
        stroke.tool,
        event.timestamp
      );
    } else {
      // We already have progressive data, just update properties if needed
      
      // Update properties in case they're different
      this.progressiveStrokeProcessor.setStrokeProperties(
        stroke.id,
        stroke.color,
        stroke.width,
        stroke.tool,
        event.timestamp
      );
    }
    
    // Save state for undo
    this.saveState();
  }
  
  // Delete stroke from canvas
  private handleStrokeDelete(event: RecordingEvent): void {
    if (event.type !== 'stroke_delete') return;
    
    const strokeId = event.data.strokeId;
    const object = this.strokeMap.get(strokeId);
    
    if (object) {
      this.canvas.remove(object);
      this.strokeMap.delete(strokeId);
      
      // Save state for undo
      this.saveState();
    }
  }
  
  // Transform stroke
  private handleStrokeTransform(event: RecordingEvent): void {
    if (event.type !== 'stroke_transform') return;
    
    const { strokeId, transform } = event.data;
    const object = this.strokeMap.get(strokeId);
    
    if (object) {
      object.set({
        left: transform.left,
        top: transform.top,
        scaleX: transform.scaleX,
        scaleY: transform.scaleY,
        angle: transform.angle
      });
      object.setCoords();
      
      // Save state for undo
      this.saveState();
    }
  }
  
  // Clear canvas
  private handleClear(): void {
    // Get all objects except background images
    const objects = this.canvas.getObjects().filter(obj => {
      // Keep question and parent images
      const objWithName = obj as any;
      return !(objWithName.name === 'questionImage' || objWithName.name === 'parentImage');
    });
    
    // Remove only strokes
    objects.forEach(obj => {
      this.canvas.remove(obj);
    });
    
    this.strokeMap.clear();
    
    // Save state for undo
    this.saveState();
  }
  
  // Handle undo
  private handleUndo(): void {
    if (this.undoStack.length <= 1) return; // Keep at least one state
    
    // Save current state to redo stack
    const currentState = this.captureState();
    this.redoStack.push(currentState);
    
    // Remove current state from undo stack
    this.undoStack.pop();
    
    // Restore previous state
    const previousState = this.undoStack[this.undoStack.length - 1];
    this.restoreState(previousState);
    
    // Show undo feedback
    this.showActionFeedback('↩ Undo');
  }
  
  // Handle redo
  private handleRedo(): void {
    if (this.redoStack.length === 0) return;
    
    // Get state from redo stack
    const redoState = this.redoStack.pop()!;
    
    // Save current state to undo stack
    this.undoStack.push(redoState);
    
    // Restore redo state
    this.restoreState(redoState);
    
    // Show redo feedback
    this.showActionFeedback('↪ Redo');
  }
  
  // Handle question change
  private handleQuestionChange(event: RecordingEvent): void {
    if (event.type !== 'question_change') return;
    
    // Clear strokes but preserve question images
    this.handleClear();
    
    // Question loading would be handled by parent component
  }
  
  // Save current state for undo
  private saveState(): void {
    const state = this.captureState();
    this.undoStack.push(state);
    
    // Limit history size
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }
    
    // Clear redo stack on new action
    this.redoStack = [];
  }
  
  // Capture current state
  private captureState(): HistoryState {
    const strokes = new Map<string, any>();
    
    this.strokeMap.forEach((object, id) => {
      strokes.set(id, {
        id,
        object: object.toObject(),
        type: object.type
      });
    });
    
    return {
      strokes,
      timestamp: Date.now()
    };
  }
  
  // Restore state
  private restoreState(state: HistoryState): void {
    // Clear current canvas
    this.canvas.clear();
    this.canvas.set('backgroundColor', 'white');
    this.strokeMap.clear();
    
    // Restore strokes
    state.strokes.forEach((strokeData, id) => {
      const enlivenObjects = (fabric.util as any).enlivenObjects;
      enlivenObjects([strokeData.object], (objects: fabric.Object[]) => {
        if (objects[0]) {
          this.canvas.add(objects[0]);
          this.strokeMap.set(id, objects[0]);
        }
      });
    });
    
    this.canvas.requestRenderAll();
  }
  
  // Show visual feedback for actions
  private showActionFeedback(text: string): void {
    const feedbackText = new fabric.Text(text, {
      left: this.canvas.getWidth() / 2 - 40,
      top: 20,
      fontSize: 18,
      fill: '#666',
      fontFamily: 'Arial',
      selectable: false,
      evented: false
    });
    
    this.canvas.add(feedbackText);
    
    // Fade out and remove
    feedbackText.animate({ opacity: 0 }, {
      duration: 1000,
      onChange: () => this.canvas.requestRenderAll(),
      onComplete: () => {
        this.canvas.remove(feedbackText);
        this.canvas.requestRenderAll();
      }
    });
  }
  
  // Get undo/redo state
  getHistoryState(): { canUndo: boolean; canRedo: boolean } {
    return {
      canUndo: this.undoStack.length > 1,
      canRedo: this.redoStack.length > 0
    };
  }
  
  // Handle stroke start
  private handleStrokeStart(event: RecordingEvent): void {
    if (event.type !== 'stroke_start') return;
    
    const { strokeId, tool, color, width, point } = event.data;
    
    // Log only in development
    if (process.env.NODE_ENV === 'development') {
    }
    
    // Store stroke metadata for potential recreation
    this.strokeMetadata.set(strokeId, {
      tool,
      color,
      width,
      startTime: event.timestamp
    });
    
    // Start progressive stroke
    this.progressiveStrokeProcessor.startProgressiveStroke(
      strokeId, tool, color, width, point, event.timestamp
    );
  }
  
  // Handle stroke add
  private handleStrokeAdd(event: RecordingEvent): void {
    if (event.type !== 'stroke_add') return;
    
    const { strokeId, point } = event.data;
    if (!strokeId) {
      return;
    }
    
    // Collect points for this stroke
    if (!this.strokePoints.has(strokeId)) {
      this.strokePoints.set(strokeId, []);
      // Silent - no log needed
    }
    this.strokePoints.get(strokeId)!.push(point);
    
    // Check if progressive stroke exists, recreate if needed
    if (!this.progressiveStrokeProcessor.hasStroke(strokeId)) {
      
      // Try to recreate from metadata
      const metadata = this.strokeMetadata.get(strokeId);
      if (metadata) {
        this.progressiveStrokeProcessor.startProgressiveStroke(
          strokeId,
          metadata.tool,
          metadata.color,
          metadata.width,
          point, // Use current point as start
          metadata.startTime
        );
      } else {
        console.error('ActionProcessor - No metadata found for stroke:', strokeId);
        return;
      }
    }
    
    // Add point to progressive processor
    this.progressiveStrokeProcessor.addPoint(strokeId, point, event.timestamp);
    
    // Silent - no need to log every point
  }
  
  // Handle stroke end
  private handleStrokeEnd(event: RecordingEvent): void {
    if (event.type !== 'stroke_end') return;
    
    const { strokeId } = event.data;
    
    // Update the stroke's actual end timestamp
    this.progressiveStrokeProcessor.setStrokeEndTime(strokeId, event.timestamp);
    
    // If we have collected points, set them
    const points = this.strokePoints.get(strokeId);
    if (points) {
      this.progressiveStrokeProcessor.setStrokePoints(strokeId, points, event.timestamp);
      this.strokePoints.delete(strokeId);
    }
    
    // Remove any preview for this stroke
    this.removeShapePreview(strokeId);
    
    // Save state for undo
    this.saveState();
  }
  
  // Handle shape preview
  private handleShapePreview(event: RecordingEvent): void {
    if (event.type !== 'shape_preview') return;
    
    const { strokeId, tool, startPoint, endPoint, points } = event.data;
    
    // Remove existing preview for this stroke
    this.removeShapePreview(strokeId);
    
    // Get stroke metadata for styling
    const metadata = this.strokeMetadata.get(strokeId);
    const color = metadata?.color || '#000000';
    const width = metadata?.width || 2;
    
    let previewObject: fabric.Object | null = null;
    
    // Create preview based on shape type
    switch (tool) {
      case 'ellipse':
        const centerX = (startPoint.x + endPoint.x) / 2;
        const centerY = (startPoint.y + endPoint.y) / 2;
        const radiusX = Math.abs(endPoint.x - startPoint.x) / 2;
        const radiusY = Math.abs(endPoint.y - startPoint.y) / 2;
        
        previewObject = new fabric.Ellipse({
          left: centerX - radiusX,
          top: centerY - radiusY,
          rx: radiusX,
          ry: radiusY,
          stroke: color,
          strokeWidth: width,
          fill: 'transparent',
          strokeLineCap: 'round' as const,
          strokeLineJoin: 'round' as const,
          strokeDashArray: [5, 5],
          selectable: false,
          evented: false,
          opacity: 0.7
        });
        break;
        
      case 'rectangle':
        const minX = Math.min(startPoint.x, endPoint.x);
        const maxX = Math.max(startPoint.x, endPoint.x);
        const minY = Math.min(startPoint.y, endPoint.y);
        const maxY = Math.max(startPoint.y, endPoint.y);
        
        previewObject = new fabric.Rect({
          left: minX,
          top: minY,
          width: maxX - minX,
          height: maxY - minY,
          stroke: color,
          strokeWidth: width,
          fill: 'transparent',
          strokeLineCap: 'round' as const,
          strokeLineJoin: 'round' as const,
          strokeDashArray: [5, 5],
          selectable: false,
          evented: false,
          opacity: 0.7
        });
        break;
        
      case 'circle':
        const radius = Math.sqrt(
          Math.pow(endPoint.x - startPoint.x, 2) + 
          Math.pow(endPoint.y - startPoint.y, 2)
        ) / 2;
        const circCenterX = (startPoint.x + endPoint.x) / 2;
        const circCenterY = (startPoint.y + endPoint.y) / 2;
        
        previewObject = new fabric.Circle({
          left: circCenterX - radius,
          top: circCenterY - radius,
          radius: radius,
          stroke: color,
          strokeWidth: width,
          fill: 'transparent',
          strokeLineCap: 'round' as const,
          strokeLineJoin: 'round' as const,
          strokeDashArray: [5, 5],
          selectable: false,
          evented: false,
          opacity: 0.7
        });
        break;
        
      default:
        // For other shapes, create a path from points
        if (points && points.length > 0) {
          let pathData = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
          for (let i = 1; i < points.length; i++) {
            pathData += ` L ${points[i].x.toFixed(2)} ${points[i].y.toFixed(2)}`;
          }
          // Close path for closed shapes
          if (tool !== 'line' && tool !== 'arrow' && tool !== 'coordinate' && tool !== 'grid') {
            pathData += ' Z';
          }
          
          previewObject = new fabric.Path(pathData, {
            stroke: color,
            strokeWidth: width,
            fill: 'transparent',
            strokeLineCap: 'round' as const,
            strokeLineJoin: 'round' as const,
            strokeDashArray: [5, 5],
            selectable: false,
            evented: false,
            opacity: 0.7
          });
        }
    }
    
    if (previewObject) {
      // Store preview reference
      (previewObject as any).strokeId = strokeId;
      (previewObject as any).isPreview = true;
      
      this.canvas.add(previewObject);
    }
  }
  
  // Remove shape preview
  private removeShapePreview(strokeId: string): void {
    const objects = this.canvas.getObjects();
    const preview = objects.find(obj => 
      (obj as any).strokeId === strokeId && 
      (obj as any).isPreview === true
    );
    
    if (preview) {
      this.canvas.remove(preview);
    }
  }
  
  // Laser pointer state
  private laserPath: fabric.Path | null = null;
  private laserPoints: { x: number; y: number }[] = [];
  private laserFadeTimeout: NodeJS.Timeout | null = null;
  
  // Handle laser start
  private handleLaserStart(event: RecordingEvent): void {
    if (event.type !== 'laser_start') return;
    
    const { point } = event.data;
    this.laserPoints = [point];
    
    // Remove any existing laser path
    if (this.laserPath) {
      this.canvas.remove(this.laserPath);
      this.laserPath = null;
    }
    
    // Clear any pending fade timeout
    if (this.laserFadeTimeout) {
      clearTimeout(this.laserFadeTimeout);
      this.laserFadeTimeout = null;
    }
  }
  
  // Handle laser move
  private handleLaserMove(event: RecordingEvent): void {
    if (event.type !== 'laser_move') return;
    
    const { point } = event.data;
    this.laserPoints.push(point);
    
    // Update laser path
    if (this.laserPath) {
      this.canvas.remove(this.laserPath);
    }
    
    // Create smooth path for laser
    if (this.laserPoints.length >= 2) {
      let pathData = `M ${this.laserPoints[0].x.toFixed(2)} ${this.laserPoints[0].y.toFixed(2)}`;
      
      // Use quadratic curves for smoother laser trail
      for (let i = 1; i < this.laserPoints.length - 1; i++) {
        const xc = (this.laserPoints[i].x + this.laserPoints[i + 1].x) / 2;
        const yc = (this.laserPoints[i].y + this.laserPoints[i + 1].y) / 2;
        pathData += ` Q ${this.laserPoints[i].x.toFixed(2)} ${this.laserPoints[i].y.toFixed(2)}, ${xc.toFixed(2)} ${yc.toFixed(2)}`;
      }
      
      // Add last point
      const lastPoint = this.laserPoints[this.laserPoints.length - 1];
      pathData += ` L ${lastPoint.x.toFixed(2)} ${lastPoint.y.toFixed(2)}`;
      
      this.laserPath = new fabric.Path(pathData, {
        stroke: '#FF0000',
        strokeWidth: 3,
        fill: '',
        opacity: 0.8,
        selectable: false,
        evented: false,
        strokeLineCap: 'round' as const,
        strokeLineJoin: 'round' as const
      });
      
      (this.laserPath as any).isLaser = true;
      this.canvas.add(this.laserPath);
    }
  }
  
  // Handle laser end
  private handleLaserEnd(event: RecordingEvent): void {
    if (event.type !== 'laser_end') return;
    
    // Start fade out after 1 second
    if (this.laserPath) {
      const path = this.laserPath;
      
      this.laserFadeTimeout = setTimeout(() => {
        // Animate fade out
        (path as any).animate('opacity', 0, {
          duration: 500,
          onChange: () => this.canvas.requestRenderAll(),
          onComplete: () => {
            this.canvas.remove(path);
            this.canvas.requestRenderAll();
          }
        });
      }, 1000);
    }
    
    // Clear points
    this.laserPoints = [];
  }
  
  // Update animations based on current time
  updateAnimations(currentTime: number): void {
    this.progressiveStrokeProcessor.updateAll(currentTime);
    
    // Periodically clean up old completed strokes
    if (Math.floor(currentTime / 10000) !== Math.floor((currentTime - 16) / 10000)) {
      this.progressiveStrokeProcessor.cleanupCompletedStrokes(currentTime);
    }
  }
  
  // Reset processor
  reset(): void {
    // Get all objects except background images
    const objects = this.canvas.getObjects().filter(obj => {
      // Keep question and parent images
      const objWithName = obj as any;
      return !(objWithName.name === 'questionImage' || objWithName.name === 'parentImage');
    });
    
    // Remove only strokes
    objects.forEach(obj => {
      this.canvas.remove(obj);
    });
    
    this.strokeMap.clear();
    this.progressiveStrokeProcessor.reset();
    this.strokePoints.clear();
    // Keep strokeMetadata for recreation - but limit its size
    if (this.strokeMetadata.size > 100) {
      // Keep only the most recent 50 strokes
      const entries = Array.from(this.strokeMetadata.entries());
      entries.sort((a, b) => b[1].startTime - a[1].startTime);
      this.strokeMetadata.clear();
      entries.slice(0, 50).forEach(([id, data]) => {
        this.strokeMetadata.set(id, data);
      });
    }
    this.undoStack = [];
    this.redoStack = [];
  }
  
  // Get stroke map for external use
  getStrokeMap(): Map<string, fabric.Object> {
    return this.strokeMap;
  }
  
  // Clear render times for backward seeking
  clearRenderTimes(): void {
    this.progressiveStrokeProcessor.clearRenderTimes();
  }
  
  // Get performance metrics
  getPerformanceMetrics(): { eventsPerSecond: number; totalEvents: number } {
    const now = Date.now();
    const elapsed = (now - this.lastMetricsReset) / 1000; // Convert to seconds
    const eventsPerSecond = elapsed > 0 ? this.eventsProcessedCount / elapsed : 0;
    
    // Reset metrics every 10 seconds to get fresh rates
    if (elapsed > 10) {
      this.eventsProcessedCount = 0;
      this.lastMetricsReset = now;
    }
    
    return {
      eventsPerSecond: Math.round(eventsPerSecond),
      totalEvents: this.eventsProcessedCount
    };
  }
}